import React from 'react';
import { Icon } from '@iconify/react';
import { renderErrorDot } from '../utils/useValidator';
import WorkOrderPartsTab from './WorkOrderPartsTab';
import WorkOrderServicesTab from './WorkOrderServicesTab';
import DocumentDataTable from './DocumentDataTable';

const WorkOrderEditModal = ({
  showModal,
  editingId,
  newWorkOrder,
  modalLoading,
  generalTabHasError,
  showGeneralOnly = false,
  showCreateEventToggle,
  onClose,
  onCreateEventToggle,
  canUpdateWorkOrder = false,
  onJobReadyStatusToggle,
  workOrderGeneralFields,
  renderGeneralFields,
  duration,
  onDurationChange,
  combinedErrors,
  onDurationBlur,
  actualDuration,
  onActualDurationChange,
  onActualDurationBlur,
  workOrderCompleteFields,
  renderCompleteFields,
  renderProgressFields,
  partsCostTotal,
  actualPartsCost,
  onActualPartsCostChange,
  labourCostTotal,
  actualLabourCost,
  onActualLabourCostChange,
  servicesCostTotal,
  actualMiscCost,
  onActualMiscCostChange,
  labourHoursTotal,
  actualLabourHours,
  onActualLabourHoursChange,
  workOrderSchedule,
  scheduleFields,
  renderScheduleField,
  workOrderParts,
  setWorkOrderParts,
  deletedPartIds,
  setDeletedPartIds,
  userProfile,
  expenseOptions,
  defaultPartsExpenseCode,
  workOrderLabour,
  setWorkOrderLabour,
  deletedLabourIds,
  setDeletedLabourIds,
  tradeOptions,
  defaultLabourExpenseCode,
  setLabourForm,
  setEditingLabourIndex,
  setLabourErrors,
  setShowLabourModal,
  isWorkOrderLocked,
  workOrderServices,
  setWorkOrderServices,
  setDeletedServiceIds,
  defaultServiceExpenseCode,
  serviceOptions,
  workOrderEquipment,
  setWorkOrderEquipment,
  deletedEquipmentIds,
  setDeletedEquipmentIds,
  setEditingEquipmentIndex,
  setEquipmentForm,
  setEquipmentErrors,
  setShowEquipmentModal,
  workOrderForms,
  onViewForm,
  onPrintForm,
  onDeleteForm,
  onOpenAddFormModal,
  workOrderDocumentFields,
  renderDocumentFields,
  onFileDelete,
  handleSave,
  showEquipmentModal,
  editingEquipmentIndex,
  equipmentForm,
  equipmentErrors,
  clearEquipmentFieldError,
  onCloseEquipmentModal,
  onSaveEquipment,
  showLabourModal,
  editingLabourIndex,
  labourForm,
  labourErrors,
  clearLabourFieldError,
  onCloseLabourModal,
  onSaveLabour,
  showFormModal,
  selectedFormId,
  formOptions,
  onSelectedFormChange,
  onCloseFormModal,
  onSaveFormSelection,
}) => (
  <>
    {showModal && (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog modal-xxl">
          <div className="modal-content" style={{ minHeight: '85vh' }}>
            <div className="modal-header">
              <h5 className="modal-title">
                {editingId ? `Edit Work Order #${newWorkOrder.workOrderNumber}` : 'New Work Order'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              {modalLoading ? (
                <div className="d-flex justify-content-center my-5">
                  <span className="spinner-border text-primary" role="status" aria-hidden="true"></span>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-">
                    <ul className="nav nav-tabs mb-0" role="tablist">
                      <li className="nav-item">
                        <button
                          className="nav-link active"
                          id="general-tab"
                          data-bs-toggle="tab"
                          data-bs-target="#general"
                          type="button"
                          role="tab"
                        >
                          General{renderErrorDot(generalTabHasError)}
                        </button>
                      </li>
                      {!showGeneralOnly && newWorkOrder.workOrderScheduleId && (
                        <li className="nav-item">
                          <button
                            className="nav-link"
                            id="schedule-tab"
                            data-bs-toggle="tab"
                            data-bs-target="#schedule"
                            type="button"
                            role="tab"
                          >
                            Schedule
                          </button>
                        </li>
                      )}
                      {!showGeneralOnly && editingId ? (
                        <>
                          <li className="nav-item">
                            <button className="nav-link" id="parts-tab" data-bs-toggle="tab" data-bs-target="#parts" type="button" role="tab">
                              Parts
                            </button>
                          </li>
                          <li className="nav-item">
                            <button className="nav-link" id="labour-tab" data-bs-toggle="tab" data-bs-target="#labour" type="button" role="tab">
                              Labour
                            </button>
                          </li>
                          <li className="nav-item">
                            <button className="nav-link" id="services-tab" data-bs-toggle="tab" data-bs-target="#services" type="button" role="tab">
                              Services
                            </button>
                          </li>
                          <li className="nav-item">
                            <button className="nav-link" id="equipment-tab" data-bs-toggle="tab" data-bs-target="#equipment" type="button" role="tab">
                              Resources
                            </button>
                          </li>
                          <li className="nav-item">
                            <button className="nav-link" id="forms-tab" data-bs-toggle="tab" data-bs-target="#forms" type="button" role="tab">
                              Forms
                            </button>
                          </li>
                          <li className="nav-item">
                            <button className="nav-link" id="documents-tab" data-bs-toggle="tab" data-bs-target="#documents" type="button" role="tab">
                              Documents
                            </button>
                          </li>
                        </>
                      ) : ''}
                    </ul>
                    <div className="d-flex align-items-center gap-3">
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-sm fw-semibold">Job Ready</span>
                        <div className="form-switch switch-success m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={Number(newWorkOrder?.jobReadyStatus) === 1}
                            disabled={!canUpdateWorkOrder}
                            onChange={(e) => onJobReadyStatusToggle && onJobReadyStatusToggle(e.target.checked ? 1 : 0)}
                          />
                        </div>
                      </div>
                      {showCreateEventToggle && (
                        <>
                          <label className="form-label mt-16" htmlFor="create-event-toggle">
                            Create Event
                          </label>
                          <div className="form-switch switch-primary d-flex align-items-center gap-3 me-16 mt-10">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="create-event-toggle"
                              checked={!!newWorkOrder.createEvent}
                              onChange={(e) => onCreateEventToggle(e.target.checked)} />
                          </div>
                        </>
                      )}
                      {newWorkOrder.workOrderScheduleId && (
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() =>
                            document
                              .getElementById('schedule-tab')
                              ?.click()
                          }
                        >
                          <Icon icon="mdi:refresh" className="text-xxl me-6" style={{ position: 'relative', top: '-1px' }} />
                          Edit Recurring
                        </button>
                      )}
                      {newWorkOrder.eventId && newWorkOrder.eventId !== '' ? (
                        <div className="alert alert-primary px-8 py-4 w-auto mb-0" role="alert">
                          <Icon icon="mdi:calendar-month-outline" className="text-xl text-primary me-6" style={{ position: 'relative', top: '-3px' }} />
                          Linked to an Event
                        </div>
                      ) : null
                      }
                    </div>
                  </div>
                  <div className="tab-content pt-3">
                    <div className="tab-pane fade show active" id="general" role="tabpanel">
                      <div className="row g-3">
                        {workOrderGeneralFields.map((f) => {
                          const showDurationField =
                            (newWorkOrder.statusId === 1 && f.field === 'scheduledAt') ||
                            (newWorkOrder.statusId === 2 && f.field === 'startedAt');
                          if (showDurationField) {
                            return (
                              <React.Fragment key={f.field}>
                                {renderGeneralFields(f)}
                                <div className="col-md-4" key="duration">
                                  <label className="form-label mb-6">Duration (hrs)</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="9999"
                                    className={`form-control ${combinedErrors.duration ? 'is-invalid' : ''}`}
                                    value={duration}
                                    onChange={(e) => onDurationChange(e.target.value)}
                                    onBlur={onDurationBlur}
                                  />
                                  {combinedErrors.duration && (
                                    <div className="invalid-feedback">{combinedErrors.duration}</div>
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          }
                          if (f.field === '__actualDuration') {
                            return (
                              <div className="col-md-4" key="actualDuration">
                                <label className="form-label mb-6">Actual Duration (hrs)</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="9999"
                                  className={`form-control ${combinedErrors.actualDuration ? 'is-invalid' : ''}`}
                                  value={actualDuration}
                                  onChange={(e) => onActualDurationChange(e.target.value)}
                                  onBlur={onActualDurationBlur}
                                />
                                {combinedErrors.actualDuration && (
                                  <div className="invalid-feedback">{combinedErrors.actualDuration}</div>
                                )}
                              </div>
                            );
                          }
                          return renderGeneralFields(f);
                        })}
                        {newWorkOrder.statusId === 4 &&
                          workOrderCompleteFields.map(renderCompleteFields)}
                        {newWorkOrder.statusId === 2 &&
                          workOrderCompleteFields.map(renderProgressFields)}
                        {editingId && (
                          <>
                            <div className="col-md-3">
                              <label className="form-label">Parts Cost</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={partsCostTotal.toFixed(2)}
                                disabled
                              />
                              <label className="form-label mt-16">Actual Parts Cost</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                value={actualPartsCost}
                                onChange={(e) => onActualPartsCostChange(e.target.value)}
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">Labour Cost</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={labourCostTotal.toFixed(2)}
                                disabled
                              />
                              <label className="form-label mt-16">Actual Labour Cost</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                value={actualLabourCost}
                                onChange={(e) => onActualLabourCostChange(e.target.value)}
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">Other Cost (Services)</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={servicesCostTotal.toFixed(2)}
                                disabled
                              />
                              <label className="form-label mt-16">Actual Misc Cost</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                value={actualMiscCost}
                                onChange={(e) => onActualMiscCostChange(e.target.value)}
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">Labour Hours</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                value={labourHoursTotal.toFixed(2)}
                                disabled
                              />
                              <label className="form-label mt-16">Actual Labour Hours</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control"
                                value={actualLabourHours}
                                onChange={(e) => onActualLabourHoursChange(e.target.value)}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    {!showGeneralOnly && newWorkOrder.workOrderScheduleId && (
                      <div className="tab-pane fade" id="schedule" role="tabpanel">
                        {workOrderSchedule ? (
                          <div className="row g-3">
                            {scheduleFields.map(renderScheduleField)}
                          </div>
                        ) : (
                          <div className="d-flex justify-content-center py-4">
                            <span className="spinner-border spinner-border-sm" />
                          </div>
                        )}
                      </div>
                    )}
                    {!showGeneralOnly && editingId ? (
                      <>
                        <div className="tab-pane fade" id="parts" role="tabpanel">
                          <WorkOrderPartsTab
                            workOrderParts={workOrderParts}
                            setWorkOrderParts={setWorkOrderParts}
                            deletedPartIds={deletedPartIds}
                            setDeletedPartIds={setDeletedPartIds}
                            newWorkOrder={newWorkOrder}
                            editingId={editingId}
                            userProfile={userProfile}
                            expenseOptions={expenseOptions}
                            defaultPartsExpenseCode={defaultPartsExpenseCode}
                          />
                        </div>
                        <div className="tab-pane fade" id="labour" role="tabpanel">
                          <div className="d-flex justify-content-end">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setLabourForm({
                                  tradeId: '',
                                  qty: '1',
                                  labourHours: '',
                                  defaultExpenseCode: defaultLabourExpenseCode ? Number(defaultLabourExpenseCode) : '',
                                  labourRate: 0,
                                });
                                setEditingLabourIndex(null);
                                setLabourErrors({});
                                setShowLabourModal(true);
                              }}
                              disabled={isWorkOrderLocked}
                            >
                              + Add Labour
                            </button>
                          </div>
                          <div className="table-responsive mt-3"
                          style={{ maxHeight: 'calc(100vh - 380px)'}}>
                            <table className="table bordered-table">
                              <thead>
                                <tr>
                                  <th>Trade</th>
                                  <th>Qty</th>
                                  <th>Hours</th>
                                  <th>Expense</th>
                                  <th>Rate</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {workOrderLabour.length === 0 && (
                                  <tr>
                                    <td colSpan="10" className="text-center py-4 rounded-0">No Labour on Work Order</td>
                                  </tr>
                                )}
                                {workOrderLabour.map((lb, idx) => (
                                  <tr key={idx}>
                                    <td>{tradeOptions.find(t => t.id === lb.tradeId)?.name || lb.tradeId} {tradeOptions.find(t => t.id === lb.tradeId)?.code || ''}</td>
                                    <td>{lb.qty}</td>
                                    <td>{lb.labourHours}</td>
                                    <td>{expenseOptions.find(e => e.id === lb.defaultExpenseCode)?.name || lb.defaultExpenseCode}</td>
                                    <td>{lb.labourRate}</td>
                                    <td>
                                      <>
                                        <button
                                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                                          onClick={() => {
                                            setEditingLabourIndex(idx);
                                            setLabourForm({
                                              ...lb,
                                              tradeId: lb.tradeId ?? '',
                                              qty:
                                                lb.qty === null || lb.qty === undefined
                                                  ? ''
                                                  : String(lb.qty),
                                              labourHours:
                                                lb.labourHours === null || lb.labourHours === undefined
                                                  ? ''
                                                  : String(lb.labourHours),
                                              defaultExpenseCode:
                                                lb.defaultExpenseCode ??
                                                (defaultLabourExpenseCode
                                                  ? Number(defaultLabourExpenseCode)
                                                  : ''),
                                              labourRate:
                                                lb.labourRate === null || lb.labourRate === undefined
                                                  ? 0
                                                  : String(lb.labourRate),
                                            });
                                            setLabourErrors({});
                                            setShowLabourModal(true);
                                          }}
                                          disabled={isWorkOrderLocked}
                                        >
                                          <Icon icon="ic:baseline-edit" />
                                        </button>
                                        <button
                                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                          onClick={() => {
                                            if (window.confirm('Delete?')) {
                                              setWorkOrderLabour(prev => prev.filter((_, i) => i !== idx));
                                              if (lb.id) setDeletedLabourIds(prev => [...prev, lb.id]);
                                            }
                                          }}
                                          disabled={isWorkOrderLocked}
                                        >
                                          <Icon icon="ic:baseline-delete-outline" />
                                        </button>
                                      </>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="tab-pane fade" id="services" role="tabpanel">
                          <WorkOrderServicesTab
                            workOrderServices={workOrderServices}
                            setWorkOrderServices={setWorkOrderServices}
                            setDeletedServiceIds={setDeletedServiceIds}
                            newWorkOrder={newWorkOrder}
                            editingId={editingId}
                            userProfile={userProfile}
                            expenseOptions={expenseOptions}
                            defaultServiceExpenseCode={defaultServiceExpenseCode}
                            serviceOptions={serviceOptions}
                          />
                        </div>
                        <div className="tab-pane fade" id="equipment" role="tabpanel">
                          <div className="d-flex justify-content-end">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setEquipmentForm({ name: '', quantity: '1', notes: '', cost: '' });
                                setEditingEquipmentIndex(null);
                                setEquipmentErrors({});
                                setShowEquipmentModal(true);
                              }}
                              disabled={isWorkOrderLocked}
                            >
                              + Add Resources
                            </button>
                          </div>
                          <div className="table-responsive mt-3"
                          style={{ maxHeight: 'calc(100vh - 380px)'}}>
                            <table className="table bordered-table">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Qty</th>
                                  {/*<th>Cost</th>*/}
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {workOrderEquipment.length === 0 && (
                                  <tr>
                                    <td colSpan="10" className="text-center py-4 rounded-0">No Resources on Work Order</td>
                                  </tr>
                                )}
                                {workOrderEquipment.map((eq, idx) => (
                                  <tr key={idx}>
                                    <td>{eq.name}</td>
                                    <td>{eq.quantity}</td>
                                    {/*<td>{eq.cost}</td>*/}
                                    <td>
                                      <>
                                        <button
                                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                                          onClick={() => {
                                            setEditingEquipmentIndex(idx);
                                            setEquipmentForm({
                                              ...eq,
                                              name: eq.name ?? '',
                                              quantity:
                                                eq.quantity === null || eq.quantity === undefined
                                                  ? ''
                                                  : String(eq.quantity),
                                              cost:
                                                eq.cost === null || eq.cost === undefined
                                                  ? ''
                                                  : String(eq.cost),
                                              notes: eq.notes ?? '',
                                            });
                                            setEquipmentErrors({});
                                            setShowEquipmentModal(true);
                                          }}
                                          disabled={isWorkOrderLocked}
                                        >
                                          <Icon icon="ic:baseline-edit" />
                                        </button>
                                        <button
                                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                          onClick={() => {
                                            if (window.confirm('Delete?')) {
                                              setWorkOrderEquipment(prev => prev.filter((_, i) => i !== idx));
                                              if (eq.id) setDeletedEquipmentIds(prev => [...prev, eq.id]);
                                            }
                                          }}
                                          disabled={isWorkOrderLocked}
                                        >
                                          <Icon icon="ic:baseline-delete-outline" />
                                        </button>
                                      </>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="tab-pane fade" id="forms" role="tabpanel">
                          <div className="d-flex justify-content-end">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={onOpenAddFormModal}
                              disabled={isWorkOrderLocked}
                            >
                              + Add Form
                            </button>
                          </div>
                          <div className="table-responsive mt-3"
                          style={{ maxHeight: 'calc(100vh - 380px)'}}>
                            <table className="table bordered-table">
                              <thead>
                                <tr>
                                  <th>Name</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {workOrderForms.length === 0 && (
                                  <tr>
                                    <td colSpan="10" className="text-center py-4 rounded-0">No Forms attached to Work Order</td>
                                  </tr>
                                )}
                                {workOrderForms.map((f) => {
                                  const isCompleted = Number(f.completed) === 1;
                                  return (
                                    <tr key={f.id}>
                                      <td>{f.form?.name || f.name}</td>
                                      <td>
                                        <button
                                          className="view-btn w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                                          onClick={() => onViewForm(f)}
                                        >
                                          <Icon icon="ic:baseline-visibility" />
                                        </button>
                                        {isCompleted && (
                                          <button
                                            className="print-btn w-32-px h-32-px bg-warning-focus text-warning-main rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                                            onClick={() => onPrintForm(f.pdfLink)}
                                          >
                                            <Icon icon="mage:printer" className="text-lg" />
                                          </button>
                                        )}
                                        {!isCompleted && (
                                          <button
                                            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                            onClick={() => onDeleteForm(f.id)}
                                            disabled={isWorkOrderLocked}
                                          >
                                            <Icon icon="ic:baseline-delete-outline" />
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="tab-pane fade" id="documents" role="tabpanel">
                          <div className="row g-3">{workOrderDocumentFields.map(renderDocumentFields)}</div>
                          {Array.isArray(newWorkOrder.workorderFiles) && newWorkOrder.workorderFiles.length > 0 && (
                            <DocumentDataTable
                              files={newWorkOrder.workorderFiles}
                              onDelete={onFileDelete}
                              directory="workorderfiles"
                            />
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>

                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </>
            </div>
          </div>
        </div>
      </div>
    )}

    {showEquipmentModal && (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog" style={{ marginTop: '10px' }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{editingEquipmentIndex !== null ? 'Edit' : 'Add'} Resources</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onCloseEquipmentModal}
              />
            </div>
            <div className="modal-body">
              <div className="mb-2">
                <label className="form-label">Name</label>
                <input
                  className={`form-control ${equipmentErrors.name ? 'is-invalid' : ''}`}
                  value={equipmentForm.name ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setEquipmentForm((prev) => ({ ...prev, name: value }));
                    clearEquipmentFieldError('name');
                  }}
                />
                {equipmentErrors.name && (
                  <div className="invalid-feedback">{equipmentErrors.name}</div>
                )}
              </div>
              <div className="mb-2">
                <label className="form-label">Quantity</label>
                <input
                  type="number"
                  className={`form-control ${equipmentErrors.quantity ? 'is-invalid' : ''}`}
                  value={equipmentForm.quantity ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setEquipmentForm((prev) => ({ ...prev, quantity: value }));
                    clearEquipmentFieldError('quantity');
                  }}
                />
                {equipmentErrors.quantity && (
                  <div className="invalid-feedback">{equipmentErrors.quantity}</div>
                )}
              </div>
              <div className="mb-2">
                <label className="form-label">Notes</label>
                <textarea className="form-control" value={equipmentForm.notes || ''} onChange={e => setEquipmentForm({ ...equipmentForm, notes: e.target.value })}></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={onCloseEquipmentModal}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={onSaveEquipment}>Save</button>
            </div>
          </div>
        </div>
      </div>
    )}

    {showLabourModal && (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{editingLabourIndex !== null ? 'Edit' : 'Add'} Labour</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onCloseLabourModal}
              />
            </div>
            <div className="modal-body">
              <div className="mb-2">
                <label className="form-label">Trade</label>
                <select
                  className={`form-select ${labourErrors.tradeId ? 'is-invalid' : ''}`}
                  value={labourForm.tradeId ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setLabourForm((prev) => ({
                      ...prev,
                      tradeId: value === '' ? '' : Number(value),
                    }));
                    clearLabourFieldError('tradeId');
                  }}
                >
                  <option value="">Select…</option>
                  {tradeOptions.map(t => (<option key={t.id} value={t.id}>{t.name} {t.code}</option>))}
                </select>
                {labourErrors.tradeId && (
                  <div className="invalid-feedback">{labourErrors.tradeId}</div>
                )}
              </div>
              <div className="mb-2">
                <label className="form-label">Quantity</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className={`form-control ${labourErrors.qty ? 'is-invalid' : ''}`}
                  value={labourForm.qty ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setLabourForm((prev) => ({ ...prev, qty: value }));
                    clearLabourFieldError('qty');
                  }}
                />
                {labourErrors.qty && (
                  <div className="invalid-feedback">{labourErrors.qty}</div>
                )}
              </div>
              <div className="mb-2">
                <label className="form-label">Hours</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className={`form-control ${labourErrors.labourHours ? 'is-invalid' : ''}`}
                  value={labourForm.labourHours ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setLabourForm((prev) => ({ ...prev, labourHours: value }));
                    clearLabourFieldError('labourHours');
                  }}
                />
                {labourErrors.labourHours && (
                  <div className="invalid-feedback">{labourErrors.labourHours}</div>
                )}
              </div>
              <div className="mb-2">
                <label className="form-label">Expense Code</label>
                <select
                  className={`form-select ${labourErrors.defaultExpenseCode ? 'is-invalid' : ''}`}
                  value={labourForm.defaultExpenseCode ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setLabourForm((prev) => ({
                      ...prev,
                      defaultExpenseCode: value === '' ? '' : Number(value),
                    }));
                    clearLabourFieldError('defaultExpenseCode');
                  }}
                >
                  <option value="">Select…</option>
                  {expenseOptions.map(ec => (<option key={ec.id} value={ec.id}>{ec.name}</option>))}
                </select>
                {labourErrors.defaultExpenseCode && (
                  <div className="invalid-feedback">{labourErrors.defaultExpenseCode}</div>
                )}
              </div>
              <div className="mb-2">
                <label className="form-label">Cost per Hour</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`form-control ${labourErrors.labourRate ? 'is-invalid' : ''}`}
                  value={labourForm.labourRate ?? ''}
                  onChange={(e) => {
                    const { value } = e.target;
                    setLabourForm((prev) => ({ ...prev, labourRate: value }));
                    clearLabourFieldError('labourRate');
                  }}
                />
                {labourErrors.labourRate && (
                  <div className="invalid-feedback">{labourErrors.labourRate}</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={onCloseLabourModal}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={onSaveLabour}>Save</button>
            </div>
          </div>
        </div>
      </div>
    )}

    {showFormModal && (
      <div className="modal modal-backdrop show d-block" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Form</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onCloseFormModal}
              />
            </div>
            <div className="modal-body">
              <div className="mb-2">
                <label className="form-label">Select Form</label>
                <select className="form-select" value={selectedFormId} onChange={(e) => onSelectedFormChange(e.target.value)}>
                  <option value="">Select…</option>
                  {formOptions.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={onCloseFormModal}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={onSaveFormSelection}>Save</button>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
);

export default WorkOrderEditModal;

export const getInitialWorkOrder = () => ({
  scheduledAt: null,
  dueDate: null,
  statusId: 1, // Open
  taskTypeId: 4, // General Maintenance
  priorityId: 3, // Medium
  statutory: 0,
  workorderFiles: [],
  workOrderImage: [],
  completionImages: [],
  createEvent: false,
  costResponsibilityId: 1,
  modifierCodeId: null,
  cycleGroupOrder: null,
});

export const mapTaskBillingToWorkOrderItems = ({
  materials,
  labour,
  services,
  equipment,
  workOrderId = null,
}) => {
  const mappedParts = Array.isArray(materials)
    ? materials
      .filter((item) => Number(item?.optional) !== 1)
      .map((item) => {
        const quantity = Number(item?.quantity);
        const partsPrice = Number(item?.partsPrice);
        return {
          id: null,
          workOrderId,
          partsId: item?.partsId ?? null,
          optional: item?.optional ?? 0,
          quantity: Number.isNaN(quantity) ? 0 : quantity,
          expenseCodeId: item?.defaultExpenseCode ?? null,
          cost: Number.isNaN(partsPrice) ? 0 : partsPrice,
          supplierId: null,
          supplier: null,
          status: 'Draft',
          warehouseId: null,
          poLineId: null,
          source: (item.stockedItem === 1
            ? 'WAREHOUSE'
            : 'PURCHASE ORDER'),
          parts: {
            id: item?.partsId ?? null,
            partNumber: item?.partNumber ?? '',
            oemNumber: '',
            description: item?.partDescription ?? '',
            lastPrice: item?.lastPrice ?? null,
          },
        };
      })
    : [];

  const mappedLabour = Array.isArray(labour)
    ? labour.map((item) => ({
      id: null,
      workOrderId,
      tradeId: item?.tradeId ?? null,
      qty: item?.qty ?? 0,
      labourHours: item?.labourHours ?? 0,
      labourRate: item?.labourRate ?? 0,
      cost: 0,
      defaultExpenseCode: item?.defaultExpenseCode ?? null,
    }))
    : [];

  const mappedServices = Array.isArray(services)
    ? services.map((item) => ({
      id: null,
      workOrderId,
      serviceId: item?.serviceId ?? null,
      defaultExpenseCode: item?.defaultExpenseCode ?? null,
      quantity: item?.quantity ?? 0,
      cost: item?.cost ?? 0,
      notes: item?.notes ?? '',
      description: item?.description ?? '',
      supplierId: item?.supplierId ?? null,
      supplier: item?.supplier ?? null,
      status: 'Draft',
      source: 'Manual',
      poLineId: null,
      purchaseOrderNumber: null,
    }))
    : [];

  const mappedEquipment = Array.isArray(equipment)
    ? equipment.map((item) => ({
      id: null,
      workOrderId,
      name: item?.name ?? '',
      quantity: item?.quantity ?? 0,
      notes: item?.notes ?? '',
      cost: 0,
    }))
    : [];

  return {
    parts: mappedParts,
    labour: mappedLabour,
    services: mappedServices,
    equipment: mappedEquipment,
  };
};
